import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faSpinner,
    faExclamationTriangle,
    faCalendar,
    faFilter,
} from '@fortawesome/free-solid-svg-icons';
import adminSecurityService from '../../../helpers/adminSecurityService';

const SecurityStats = () => {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        days: 7, // Asegurar que sea número
        marcaId: '',
    });

    useEffect(() => {
        fetchStats();
    }, [filters.days, filters.marcaId]);

    const fetchStats = async () => {
        setLoading(true);
        setError(null);
        try {
            // Asegurar que days sea un número válido
            const daysNumber = typeof filters.days === 'number'
                ? filters.days
                : parseInt(filters.days, 10) || 7;

            const data = await adminSecurityService.getSecurityStats({
                days: daysNumber,
                marcaId: filters.marcaId || null,
            });
            setStats(data);
        } catch (err) {
            console.error('Error fetching security stats:', err);
            setError(err.message || 'Error al cargar estadísticas');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl text-gray-400" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex items-start gap-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mt-0.5" />
                    <div>
                        <h5 className="font-semibold text-red-900 mb-1">Error al cargar estadísticas</h5>
                        <p className="text-sm text-red-800">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!stats) {
        return null;
    }

    return (
        <div className="space-y-6">
            {/* Filters */}
            <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faCalendar} className="text-gray-400" />
                        <label htmlFor="days" className="text-sm font-medium text-gray-700">
                            Período:
                        </label>
                        <select
                            id="days"
                            value={filters.days}
                            onChange={(e) => {
                                const daysValue = parseInt(e.target.value, 10);
                                setFilters({ ...filters, days: isNaN(daysValue) ? 7 : daysValue });
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        >
                            <option value={7}>7 días</option>
                            <option value={30}>30 días</option>
                            <option value={90}>90 días</option>
                        </select>
                    </div>
                    <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faFilter} className="text-gray-400" />
                        <label htmlFor="marcaId" className="text-sm font-medium text-gray-700">
                            Marca (opcional):
                        </label>
                        <input
                            type="text"
                            id="marcaId"
                            value={filters.marcaId}
                            onChange={(e) => setFilters({ ...filters, marcaId: e.target.value })}
                            placeholder="ID de marca"
                            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        />
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <p className="text-sm font-medium text-gray-600">Total de Clientes</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalClients || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">Período: {stats.period}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <p className="text-sm font-medium text-gray-600">Clientes Sospechosos</p>
                    <p className="text-3xl font-bold text-yellow-600 mt-2">
                        {stats.suspiciousClients || 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                        {stats.totalClients > 0
                            ? `${((stats.suspiciousClients / stats.totalClients) * 100).toFixed(1)}% del total`
                            : '0%'}
                    </p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <p className="text-sm font-medium text-gray-600">IPs Bloqueadas</p>
                    <p className="text-3xl font-bold text-red-600 mt-2">{stats.blockedIPs || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">Total activas</p>
                </div>
            </div>

            {/* Patterns Distribution */}
            {stats.patterns && Object.keys(stats.patterns).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="p-6 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Distribución de Patrones</h3>
                        <p className="text-sm text-gray-600 mt-1">
                            Patrones de spam detectados en los últimos {filters.days} días
                        </p>
                    </div>
                    <div className="p-6">
                        <div className="space-y-4">
                            {Object.entries(stats.patterns)
                                .sort(([, a], [, b]) => b - a)
                                .map(([pattern, count]) => {
                                    const percentage =
                                        stats.suspiciousClients > 0
                                            ? ((count / stats.suspiciousClients) * 100).toFixed(1)
                                            : 0;
                                    return (
                                        <div key={pattern}>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-sm font-medium text-gray-700">{pattern}</span>
                                                <span className="text-sm text-gray-900">
                                                    {count} ({percentage}%)
                                                </span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                <div
                                                    className="bg-red-600 h-2 rounded-full transition-all"
                                                    style={{
                                                        width: `${percentage}%`,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                </div>
            )}

            {/* Daily Clients Chart */}
            {stats.dailyClients && stats.dailyClients.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="p-6 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Actividad Diaria</h3>
                        <p className="text-sm text-gray-600 mt-1">
                            Clientes creados por día (últimos {filters.days} días)
                        </p>
                    </div>
                    <div className="p-6">
                        <div className="flex items-end justify-between gap-2 h-64">
                            {stats.dailyClients.map((day, index) => {
                                const maxCount = Math.max(...stats.dailyClients.map((d) => d.count));
                                const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                                return (
                                    <div key={index} className="flex-1 flex flex-col items-center gap-2">
                                        <div className="w-full flex flex-col items-center justify-end h-full">
                                            <div
                                                className="w-full bg-blue-600 rounded-t transition-all hover:bg-blue-700 cursor-pointer"
                                                style={{ height: `${height}%` }}
                                                title={`${day.count} clientes el ${new Date(day._id).toLocaleDateString('es-ES')}`}
                                            />
                                        </div>
                                        <span className="text-xs text-gray-600">
                                            {new Date(day._id).toLocaleDateString('es-ES', {
                                                day: '2-digit',
                                                month: '2-digit',
                                            })}
                                        </span>
                                        <span className="text-xs font-medium text-gray-900">{day.count}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Top Blocked IPs */}
            {stats.topBlockedIPs && stats.topBlockedIPs.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="p-6 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Top IPs Bloqueadas</h3>
                        <p className="text-sm text-gray-600 mt-1">IPs con más intentos bloqueados</p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        IP
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Intentos
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Razón
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Tipo
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Fecha
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {stats.topBlockedIPs.map((ipData, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                                            {ipData.ip}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                {ipData.blockedAttempts || 0}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                                            {ipData.reason}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {ipData.isPermanent ? (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                    Permanente
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                    Temporal
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {ipData.createdAt
                                                ? new Date(ipData.createdAt).toLocaleDateString('es-ES')
                                                : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SecurityStats;

